import "./HeaderMobile.scss";

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Calendar } from "primereact/calendar";
import { addLocale } from "primereact/api";
import { getFirstName } from "../../../../../commons/util/HallAppUtil";

const STORAGE_AGENDA_STEP = "hallapp_agenda_step_minutes"; // "60" | "30" | "15"
const STORAGE_ONLY_BOOKED = "hallapp_agenda_only_booked"; // "1" | "0"

function HeaderMobile({
  currentDate,
  onDateChange,
  isAdminOrReception = true,
  employeeSelected,
  employees = [],
  onEmployeeSelect,
}) {
  const [selectedDate, setSelectedDate] = useState(currentDate);
  const [visibleCalendar, setVisibleCalendar] = useState(false);
  const [visibleEmployeeModal, setVisibleEmployeeModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // bottom sheet (config)
  const [showConfigSheet, setShowConfigSheet] = useState(false);
  const configDialogRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const [agendaStep, setAgendaStep] = useState("30");
  const [onlyBooked, setOnlyBooked] = useState(true);

  addLocale("pt-BR", {
    firstDayOfWeek: 0,
    dayNames: ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"],
    dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
    dayNamesMin: ["D", "S", "T", "Q", "Q", "S", "S"],
    monthNames: [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ],
    monthNamesShort: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
    today: "Hoje",
    clear: "Limpar",
  });

  // carrega preferências do localStorage
  useEffect(() => {
    try {
      const savedStep = localStorage.getItem(STORAGE_AGENDA_STEP);
      const savedOnly = localStorage.getItem(STORAGE_ONLY_BOOKED);

      if (savedStep === "60" || savedStep === "30" || savedStep === "15") {
        setAgendaStep(savedStep);
      }

      if (savedOnly === "1" || savedOnly === "0") {
        setOnlyBooked(savedOnly === "1");
      }
    } catch (_) {
      // ignore
    }
  }, []);

  // salva preferências
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_AGENDA_STEP, String(agendaStep));
      localStorage.setItem(STORAGE_ONLY_BOOKED, onlyBooked ? "1" : "0");
    } catch (_) {
      // ignore
    }
  }, [agendaStep, onlyBooked]);

  // mantém selectedDate sincronizado com currentDate
  useEffect(() => {
    setSelectedDate(currentDate);
  }, [currentDate]);

  const monthNames = useMemo(
    () => [
      "Janeiro",
      "Fevereiro",
      "Março",
      "Abril",
      "Maio",
      "Junho",
      "Julho",
      "Agosto",
      "Setembro",
      "Outubro",
      "Novembro",
      "Dezembro",
    ],
    []
  );

  const month = monthNames[currentDate.getMonth()];

  const handleDateSelect = (e) => {
    setSelectedDate(e.value);
    if (onDateChange) onDateChange(e.value);

    setTimeout(() => {
      setVisibleCalendar(false);
    }, 250);
  };

  const handleToday = () => {
    const today = new Date();
    setSelectedDate(today);
    if (onDateChange) onDateChange(today);

    setTimeout(() => {
      setVisibleCalendar(false);
    }, 250);
  };

  const openEmployeeModal = () => {
    if (isAdminOrReception && employees.length > 1) {
      setVisibleEmployeeModal(true);
      setSearchTerm("");
    }
  };

  const closeEmployeeModal = () => {
    setVisibleEmployeeModal(false);
    setSearchTerm("");
  };

  const handleEmployeeSelect = (employee) => {
    if (onEmployeeSelect) onEmployeeSelect(employee);
    closeEmployeeModal();
  };

  const filteredEmployees = employees.filter((emp) => {
    const n = (emp.name || "").toLowerCase();
    const e = (emp.email || "").toLowerCase();
    const s = (searchTerm || "").toLowerCase();
    return n.includes(s) || e.includes(s);
  });

  function getInitial(name) {
    if (!name) return "";
    return name.trim().charAt(0).toUpperCase();
  }

  // ===== config sheet handlers =====
  const openConfigSheet = () => {
    lastFocusedRef.current = document.activeElement;
    setShowConfigSheet(true);
  };

  const closeConfigSheet = () => {
    setShowConfigSheet(false);
    requestAnimationFrame(() => {
      if (lastFocusedRef.current && lastFocusedRef.current.focus) {
        lastFocusedRef.current.focus();
      }
    });
  };

  // acessibilidade: ESC fecha
  useEffect(() => {
    if (!showConfigSheet) return;

    requestAnimationFrame(() => {
      configDialogRef.current?.focus();
    });

    const onKey = (e) => {
      if (e.key === "Escape") closeConfigSheet();
    };

    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [showConfigSheet]);

  return (
    <>
      <header className="header-mobile">
        <div className="header-mobile__left">
          <div
            className="header-mobile__date"
            onClick={() => setVisibleCalendar(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setVisibleCalendar(true);
            }}
            aria-label="Selecionar data"
          >
            <span className="header-mobile__month">{month}</span>
          </div>
        </div>

        <div className="header-mobile__right">
          <div className="header-mobile__userWrap">
            <div
              className={`header-mobile__avatar ${isAdminOrReception && employees.length > 1 ? "header-mobile__avatar--clickable" : ""
                }`}
              onClick={openEmployeeModal}
              role={isAdminOrReception && employees.length > 1 ? "button" : undefined}
              tabIndex={isAdminOrReception && employees.length > 1 ? 0 : undefined}
              onKeyDown={(e) => {
                if (!isAdminOrReception || employees.length <= 1) return;
                if (e.key === "Enter" || e.key === " ") openEmployeeModal();
              }}
              aria-haspopup="dialog"
              aria-label="Selecionar funcionário"
            >
              <span className="header-mobile__userName">{getFirstName(employeeSelected?.name)}</span>
              <div className="header-mobile__avatar-initial">{getInitial(employeeSelected?.name || "H")}</div>
            </div>

            <button
              type="button"
              className="header-mobile__gearBtn"
              onClick={openConfigSheet}
              aria-label="Configurações da agenda"
              title="Configurações da agenda"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.1 7.1 0 0 0-1.63-.94l-.36-2.54A.5.5 0 0 0 13.9 1h-3.8a.5.5 0 0 0-.49.42l-.36 2.54c-.58.22-1.12.52-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.71 7.48a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58a.5.5 0 0 0-.12.64l1.92 3.32c.13.22.39.31.6.22l2.39-.96c.51.41 1.05.73 1.63.94l.36 2.54c.04.24.25.42.49.42h3.8c.24 0 .45-.18.49-.42l.36-2.54c.58-.22 1.12-.52 1.63-.94l2.39.96c.22.09.47 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8a3.5 3.5 0 0 1 0 7.5Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Modal calendário */}
      {visibleCalendar && (
        <div className="calendar-modal-wrapper">
          <div className="calendar-modal-wrapper__overlay" onClick={() => setVisibleCalendar(false)} />
          <div className="calendar-modal-wrapper__content">
            <div className="calendar-modal-wrapper__header">
              <button
                className="calendar-modal-wrapper__close"
                onClick={() => setVisibleCalendar(false)}
                aria-label="Fechar calendário"
              >
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>

              <div className="calendar-modal-wrapper__header-info">
                <h2 className="calendar-modal-wrapper__title">Selecione uma data</h2>
                <p className="calendar-modal-wrapper__subtitle">
                  {selectedDate.toLocaleDateString("pt-BR", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
            </div>

            <div className="calendar-modal-wrapper__body">
              <Calendar
                value={selectedDate}
                onChange={handleDateSelect}
                inline
                locale="pt-BR"
                showWeek={false}
                monthNavigator
                yearNavigator
                yearRange="2025:2030"
              />
            </div>

            <div className="calendar-modal-wrapper__footer">
              <button
                className="calendar-modal-wrapper__btn calendar-modal-wrapper__btn--secondary"
                onClick={() => setVisibleCalendar(false)}
              >
                Cancelar
              </button>
              <button
                className="calendar-modal-wrapper__btn calendar-modal-wrapper__btn--primary"
                onClick={handleToday}
              >
                Hoje
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal seleção funcionário */}
      {visibleEmployeeModal && (
        <div className="employee-modal-wrapper">
          <div className="employee-modal-wrapper__overlay" onClick={closeEmployeeModal} />
          <div
            className="employee-modal-wrapper__content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-modal-title"
          >
            <div className="employee-modal-wrapper__header">
              <h2 id="employee-modal-title">Selecione um funcionário</h2>
              <button className="employee-modal-wrapper__close" onClick={closeEmployeeModal} aria-label="Fechar modal">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            <input
              type="text"
              className="employee-modal-wrapper__search"
              placeholder="Pesquisar funcionário..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
              aria-label="Pesquisar funcionário"
            />

            <ul className="employee-modal-wrapper__list">
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((emp) => (
                  <li
                    key={emp.id}
                    className={`employee-modal-wrapper__item ${employeeSelected?.id === emp.id ? "selected" : ""}`}
                    onClick={() => handleEmployeeSelect(emp)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") handleEmployeeSelect(emp);
                    }}
                  >
                    <div className="employee-modal-wrapper__name">{emp.name}</div>
                    <div className="employee-modal-wrapper__email">{emp.email}</div>
                  </li>
                ))
              ) : (
                <li className="employee-modal-wrapper__no-results">Nenhum funcionário encontrado</li>
              )}
              <div style={{ height: "calc(50px + 72px)" }}></div>
            </ul>
          </div>
        </div>
      )}

      {/* Bottom Sheet - Configurações da agenda */}
      {showConfigSheet && (
        <div className="hmSheet" onClick={(e) => (e.target === e.currentTarget ? closeConfigSheet() : null)}>
          <div
            className="hmSheet__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hmSheetTitle"
            tabIndex={-1}
            ref={configDialogRef}
          >
            <div className="hmSheet__grab" aria-hidden="true" />
            <div className="hmSheet__header">
              <div>
                <div id="hmSheetTitle" className="hmSheet__title">Configurações da agenda</div>
                <div className="hmSheet__subtitle">Personalize como você quer visualizar</div>
              </div>

              <button type="button" className="hmSheet__close" onClick={closeConfigSheet} aria-label="Fechar">
                <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            <div className="hmSheet__body">
              {/* 1) Step */}
              <div className="hmField">
                <div className="hmField__label">Intervalo de exibição</div>
                <div className="hmField__help">Defina de quantos em quantos minutos os horários aparecem.</div>

                <div className="hmChips" role="radiogroup" aria-label="Intervalo de exibição">
                  {[
                    { v: "60", label: "1h" },
                    { v: "30", label: "30min" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      className={`hmChip ${agendaStep === opt.v ? "is-active" : ""}`}
                      role="radio"
                      aria-checked={agendaStep === opt.v}
                      onClick={() => setAgendaStep(opt.v)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* 2) only booked */}
              <div className="hmField">
                <div className="hmRow">
                  <div>
                    <div className="hmField__label">Mostrar apenas horários de atendimento</div>
                    <div className="hmField__help">
                      Oculta horários fora do período de atendimento configurado.
                    </div>
                  </div>

                  <button
                    type="button"
                    className={`hmToggle ${onlyBooked ? "is-on" : ""}`}
                    onClick={() => setOnlyBooked((p) => !p)}
                    role="switch"
                    aria-checked={onlyBooked}
                    aria-label="Mostrar somente horários com agendamento"
                  >
                    <span className="hmToggle__knob" />
                  </button>
                </div>
              </div>
            </div>

            <div className="hmSheet__footer">
              <button type="button" className="hmBtn hmBtn--ghost" onClick={closeConfigSheet}>
                Fechar
              </button>
              <button
                type="button"
                className="hmBtn hmBtn--primary"
                onClick={() => {
                  closeConfigSheet()
                  window.location.reload();
                }}
                title="As preferências já são salvas automaticamente"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default HeaderMobile;
